import React, { useEffect, useRef, useState, forwardRef } from "react";
import Image from "../images/Image";
import InputLabel from "../inputfields/InputLabel";
import SearchWithDropdown from "./SearchWithDropdown";
import DropDownModel from "./DropDownModel";
import ErrorMessage from "../ErrorMessages/ErrorMessage";
import DropdownFramer from "../framerMotion/DropdownFramer";
import InputShimmer from "../inputfields/InputShimmer";
import { inputFieldContent } from "../inputfields/InputFieldContent";
import useTranslation from "../CustomHooks/useTranslation";
import ToolTip from "../ToolTip/ToolTip";


interface DropdownButtonProps {
  labelName: string;
  id: string;
  errorMessage?: string;
  required?: boolean;
  onChange?: (id: string, value: string) => void; // Accepts both id and value
  tabIndex: number;
  options: string[];
  selectedValue?: string;
  handleSearchValue?: (value: string, id: string) => void;
  disabled?: boolean;
  noSearch?: boolean;
  className?: string;
  editLoading?: boolean;
  modalStyle?: string;
  customWidth?: boolean;
  loadMoreOptions?: (params: any, isPageNumberChange?: boolean) => void;
  testId?: string;
  setSearchedAccount?: (value: string) => void;
  dropdownTestId?: string;
  dropdownTopClass?: string;
  dropDownOpen?: boolean;
  setDropDownOpen?: (value: boolean) => void;
  toolTipClassName?: string;
  placeholder?: string;
  onOpen?: () => void;
  width?: string;
  notAbsolute?: boolean;
  height?: string;
  dynamicDropdown?: boolean;
}

interface DropdownButtonForSearchProps extends DropdownButtonProps {
  useOutsideClickClose?: boolean;
}

const DropdownButtonForSearch = forwardRef<HTMLDivElement, DropdownButtonForSearchProps>(function DropdownButtonForSearch({
  labelName,
  id,
  errorMessage,
  dropdownTopClass,
  required,
  onChange,
  // handleSearchValue = () => null,
  options,
  selectedValue,
  disabled,
  noSearch = false,
  editLoading,
  className,
  modalStyle,
  customWidth,
  testId,
  loadMoreOptions,
  setSearchedAccount,
  dropdownTestId,
  dropDownOpen,
  toolTipClassName,
  setDropDownOpen,
  placeholder,
  width,
  notAbsolute,
  useOutsideClickClose = true,
  onOpen,
  height,
  dynamicDropdown = false
}, ref) {
  const [isOpen, setIsOpen] = useState(false);
  const localRef = useRef<HTMLDivElement>(null);
  const dropdownRef = (ref as React.RefObject<HTMLDivElement>) || localRef;
  const [buttonWidth, setButtonWidth] = useState(0)
  const { translate } = useTranslation(inputFieldContent);
  const [searchValue, setSearchValue] = useState('');
  const [dropdownKey, setDropdownKey] = useState(0);
  const handleSearchValue = async (value: string, id: string) => {
    setSearchValue(value);
    setSearchedAccount?.(value);
    if (loadMoreOptions) {
      await loadMoreOptions(value, false);
    }
    // Removed loadMoreOptions call here to prevent API call on every search or close
  }
  const handleSelect = (value?: string | Record<string, any>, action?: boolean) => {
    if (onChange && typeof value === 'string') {
      onChange(id, value); // Pass both `id` and `value`
    }
    setSearchValue(''); // Clear search when option is selected
    setIsOpen(!!action);
    if (setDropDownOpen) {
      setDropDownOpen(!!action);
    }
  };

  const handleSearch = async (value: string) => {
    await handleSearchValue(value, id);
  };
  const handleClose = (action: boolean) => {
    if (action) {
      setDropdownKey(prev => prev + 1); // Force re-render when opening
    }
    loadMoreOptions?.("", false);
    setIsOpen(action);
    if (setDropDownOpen) {
      setDropDownOpen(action);
    }
  };

  // Outside click close for multiple dropdowns
  useEffect(() => {
    if (!useOutsideClickClose) return;
    function handleClickOutside(event: MouseEvent) {
      if (
        (dropDownOpen ?? isOpen) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropDownOpen?.(false);
        setIsOpen(false);
      }
    }
    if (dropDownOpen ?? isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropDownOpen, isOpen, setDropDownOpen, dropdownRef, useOutsideClickClose]);

  useEffect(() => {
    if (dropdownRef.current) {
      setButtonWidth(dropdownRef.current.offsetWidth);
    }
  }, [isOpen, dropDownOpen, dropdownRef]);
  return (
    <DropDownModel isOpen={dropDownOpen ? dropDownOpen : isOpen} handleClose={handleClose} modalStyle={modalStyle}>
      <div ref={dropdownRef}>
        <InputLabel labelName={labelName} id={id} required={required} />

        {editLoading ? <InputShimmer className={className} /> : <>
          <button
            id={id}
            type="button"
            data-testid={testId || id}
            className={`${className} bg-white ${width || 'w-80'} ${height || 'h-14'} py-5 pr-[15px] disabled:bg-disable_fill disabled:border-disable_stroke disabled:cursor-not-allowed pl-2.5 focus:pl-[9px] border rounded-lg text-subTitle outline-none focus:border-2 flex justify-between items-center ${errorMessage ? "border-negative" : "border-P_light_active"
              }`}
            disabled={disabled}
            onClick={() => {
              if (!(dropDownOpen ? dropDownOpen : isOpen) && onOpen) onOpen();
              handleClose(dropDownOpen ? !dropDownOpen : !isOpen);
            }}
          // tabIndex={tabIndex}
          >
            <span
              className={`${selectedValue ? "text-primary_text" : "text-secondary_text"} group w-[95%] truncate flex flex-1 text-subTitle`}
            >
              <p className=" truncate">{selectedValue || translate(`Select ${placeholder ? placeholder : labelName.toLowerCase()}`)}</p>
              {selectedValue && <ToolTip ToolTipItem={selectedValue || ""} top={`left-0 ${toolTipClassName ? toolTipClassName : 'top-[1rem]'} `} />}
            </span>
            {!disabled && <DropdownFramer
              isDropdownVisible={dropDownOpen ? dropDownOpen : isOpen}
            >
              <Image src={disabled ? "arrow_light" : "dropdown-down-arrow"} />
            </DropdownFramer>}
          </button>
        </>}
        {(dropDownOpen || isOpen) && (
          <SearchWithDropdown
            key={dropdownKey}
            options={options}
            handleSearchValue={handleSearch}
            id={id}
            onSelect={handleSelect} // Pass `handleSelect` as the callback
            noSearch={noSearch}
            className={className}
            style={{ width: buttonWidth }}
            isInfiniteDropdown={true}
            fetchMoreData={async (value, isPageNumberChange) => {
              if (loadMoreOptions) {
                await loadMoreOptions(value, isPageNumberChange);
              }
            }}
            dropdownTestId={dropdownTestId}
            dropdownTopClass={dropdownTopClass}
            notAbsolute={notAbsolute}
            dynamicDropdown={dynamicDropdown}
          />
        )}
        {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
      </div>
    </DropDownModel>
  );
});

export default DropdownButtonForSearch;
